/**
 * The English catalogue, statically; every other language on demand.
 *
 * English is imported statically, which keeps the no-flash property: it is the
 * fallback, so it is already there while anything else loads, and `tsc`
 * derives the key types from these files (see `i18next.d.ts`).
 *
 * The other fourteen are lazy chunks fetched when the language is chosen or
 * restored, so nobody downloads catalogues they will never switch to. Adding a
 * language means a folder under `locales/<tag>/` with one file per namespace
 * plus an entry in `languages.ts` — `loadLanguageBundle` below picks the files
 * up without further wiring.
 */

import enAssistant from "./locales/en/assistant.json";
import enCommon from "./locales/en/common.json";
import enErrors from "./locales/en/errors.json";
import enMessages from "./locales/en/messages.json";
import enTickets from "./locales/en/tickets.json";
import enUsage from "./locales/en/usage.json";

/**
 * The namespaces, in the order they are registered with i18next.
 *
 * - `common`    — shell, navigation, buttons, generic states, dates, a11y labels
 * - `tickets`   — the ticket list, ticket detail, and every Ivanti-facing enum
 * - `assistant` — the conversational surface
 * - `errors`    — one entry per API error code under `code.<CODE>`
 * - `messages`  — the Message Center: notices, kinds, placements, admin screen
 * - `usage`     — the admin Usage page: agent status, sign-ins, time on page
 */
export const APP_NAMESPACES = [
  "common",
  "tickets",
  "assistant",
  "errors",
  "messages",
  "usage",
] as const;

export type AppNamespace = (typeof APP_NAMESPACES)[number];

/** The namespace assumed when `useT()` is called without an argument. */
export const DEFAULT_NAMESPACE = "common" satisfies AppNamespace;

/** The English bundle. Also the shape every other language is typed against. */
export const enResources = {
  common: enCommon,
  tickets: enTickets,
  assistant: enAssistant,
  errors: enErrors,
  messages: enMessages,
  usage: enUsage,
} as const;

/**
 * What i18next starts with: English, and nothing else.
 *
 * Every other language arrives through `loadLanguageBundle`.
 */
export const resources = { en: enResources } as const;

/**
 * Every non-English catalogue on disk, as a lazy chunk each.
 *
 * `import.meta.glob` is statically analysable, so Vite emits one chunk per
 * file and the main bundle carries none of them.
 */
const CATALOGUES = import.meta.glob<{ default: Record<string, unknown> }>([
  "./locales/*/*.json",
  // English is imported statically above. Globbing it too would make it both
  // static and dynamic, which Vite reports as an ineffective dynamic import.
  "!./locales/en/*.json",
]);

/**
 * Fetch one language's namespaces.
 *
 * Args:
 *   tag: an `APP_LANGUAGES` tag, e.g. `de`.
 *
 * Returns:
 *   Namespace name -> catalogue, ready for `i18next.addResourceBundle`. An
 *   unknown tag yields an empty object rather than throwing: a missing
 *   catalogue should fall back to English, not break the page.
 */
export async function loadLanguageBundle(tag: string): Promise<Record<string, unknown>> {
  const prefix = `./locales/${tag}/`;
  const loaded = await Promise.all(
    Object.entries(CATALOGUES)
      .filter(([path]) => path.startsWith(prefix))
      .map(async ([path, load]) => {
        const namespace = path.slice(prefix.length).replace(/\.json$/, "");
        const module = await load();
        return [namespace, module.default] as const;
      }),
  );
  return Object.fromEntries(loaded);
}
